import { Box, Paper, IconButton, Tooltip } from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";

import { useEffect, useState } from "react";
import { getLoans } from "../../api/api";
import { useNavigate } from "react-router-dom";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";

import DataTable from "../../components/datatable/DataTable";
import StatusChip from "../../components/datatable/StatusChip";
import FilterBar from "../../components/common/FilterBar";

import { GridColDef } from "@mui/x-data-grid";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

const DisbursementList = () => {
  const [data, setData] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 🔥 FETCH
  const fetchData = async () => {
    try {
      setLoading(true);

      // 🔥 DEFAULT STATUS
      let finalStatus = "disbursed";

      // 🔥 FILTER OVERRIDE
      if (status) {
        finalStatus = status;
      }

      const res = await getLoans({
        search,
        status: finalStatus,
      });

      setData(res.data?.data || []);
    } catch {
      dispatch(
        showSnackbar({
          message: "Failed to fetch disbursement data",

          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(fetchData, 400);
    return () => clearTimeout(delay);
  }, [search, status]);

  // 🔥 COLUMNS
  const columns: GridColDef[] = [
    {
      field: "leadCode",
      headerName: "Lead ID",
      flex: 1,
      renderCell: (params) => params.row?.leadId?.leadCode || "-",
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1,
      renderCell: (params) => params.row?.leadId?.customerName || "-",
    },
    {
      field: "mobile",
      headerName: "Mobile",
      flex: 1,
      renderCell: (params) => params.row?.leadId?.mobile || "-",
    },
    {
      field: "productType",
      headerName: "Product",
      flex: 1,
      renderCell: (params) => params.row?.leadId?.productType || "-",
    },
    {
      field: "loanAmount",
      headerName: "Loan Amount",
      flex: 1,
      renderCell: (params) => {
        const amount = params.row?.loanAmount;
        return amount ? `₹ ${Number(amount).toLocaleString("en-IN")}` : "-";
      },
    },

    // 🔥 NET DISBURSEMENT
    {
      field: "netDisbursement",
      headerName: "Net Disbursement",
      flex: 1,
      renderCell: (params) => {
        const net =
          params.row?.disbursement?.netDisbursement ||
          params.row?.netDisbursement;

        return net ? `₹ ${Number(net).toLocaleString("en-IN")}` : "-";
      },
    },

    // 🟣 NEW: PDD STATUS
    {
      field: "pddStatus",
      headerName: "PDD",
      flex: 1,
      renderCell: (params) => {
        const status = params.row?.disbursement?.pdd?.status || "pending";

        return <StatusChip status={status} />;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <StatusChip status={params.row?.leadId?.status} />
      ),
    },

    // 🟣 NEW: DISBURSEMENT DATE
    {
      field: "disbursementDate",
      headerName: "Date",
      flex: 1,
      renderCell: (params) => {
        const date = params.row?.disbursement?.disbursementDate;

        return date ? new Date(date).toLocaleDateString() : "-";
      },
    },

    // 🔥 ACTIONS
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;

        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip
              title={
                row?.leadId?.status === "approved"
                  ? "Create Disbursement"
                  : "View Disbursement"
              }
            >
              <IconButton
                size="small"
                onClick={() =>
                  navigate(
                    row?.leadId?.status === "approved"
                      ? `/disbursement/create/${row._id}`
                      : `/disbursement/view/${row._id}`,
                  )
                }
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* 💰 COMMISSION */}
            {row?.leadId?.status === "disbursed" && (
              <Tooltip
                title={row?.commission ? "View Commission" : "Add Commission"}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(
                      row?.commission
                        ? `/commission/view/${row._id}`
                        : `/commission/create/${row._id}`,
                    )
                  }
                  sx={{
                    background: row?.commission ? "#ecfdf5" : "#e0f2fe",

                    color: row?.commission ? "#16a34a" : "#0284c7",
                  }}
                >
                  <CurrencyRupeeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 1 }}>
      <FilterBar
        title="Disbursement Management"
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
      />

      <Paper
        sx={{
          mt: 2,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        <DataTable rows={data} columns={columns} loading={loading} />
      </Paper>
    </Box>
  );
};

export default DisbursementList;
